import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Twitter, Facebook, Youtube } from "lucide-react";
import { WHATSAPP_NUMBER } from "@/data/sample-listings";

const footerLinks = {
  explore: [
    { label: "All Tours", href: "/tours" },
    { label: "Event Centres", href: "/tours?category=event-centre" },
    { label: "Hotels", href: "/tours?category=hotel" },
    { label: "Apartments", href: "/tours?category=apartment" },
  ],
  cities: [
    { label: "Abuja", href: "/tours?city=abuja" },
    { label: "Lagos", href: "/tours?city=lagos" },
    { label: "Jos", href: "/tours?city=jos" },
    { label: "Port Harcourt", href: "/tours?city=port-harcourt" },
  ],
  company: [
    { label: "About Us", href: "/about" },
    { label: "Vista360 TV", href: "/tv" },
    { label: "Pricing", href: "/pricing" },
    { label: "Contact", href: "/contact" },
  ],
  legal: [
    { label: "Terms & Conditions", href: "/terms" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Refund Policy", href: "/refund-policy" },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-foreground text-background">
      <div className="container-tight py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center">
                <span className="text-foreground font-display font-bold text-lg">V</span>
              </div>
              <div>
                <span className="font-display font-bold text-xl text-background">Vista360</span>
                <span className="font-display font-bold text-xl text-secondary">.NG</span>
              </div>
            </Link>
            <p className="text-background/70 mb-6 max-w-sm">
              Nigeria's premier 360° virtual tour platform. Experience properties like never before, 
              from anywhere in the world.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-background/70 hover:text-secondary transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>+234 803 286 0062</span>
              </a>
              <a 
                href="mailto:vista360nigeriaenterprise@vista360.ng"
                className="flex items-center gap-3 text-background/70 hover:text-secondary transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>vista360nigeriaenterprise@vista360.ng</span>
              </a>
              <div className="flex items-center gap-3 text-background/70">
                <MapPin className="w-4 h-4" />
                <span>Garki Area 11, Abuja</span>
              </div>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-4">Explore</h4>
            <ul className="space-y-3">
              {footerLinks.explore.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-background/70 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Cities */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-4">Cities</h4>
            <ul className="space-y-3">
              {footerLinks.cities.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-background/70 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-background/70 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-background/70 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-background/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-background/50 text-sm">
            © {new Date().getFullYear()} Vista360 NG. All rights reserved.
          </p>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a 
              href="#" 
              aria-label="Follow Vista360 on Instagram"
              className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-foreground transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a 
              href="#" 
              aria-label="Follow Vista360 on Twitter"
              className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-foreground transition-colors"
            >
              <Twitter className="w-5 h-5" />
            </a>
            <a 
              href="#" 
              aria-label="Follow Vista360 on Facebook"
              className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-foreground transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a 
              href="#" 
              aria-label="Follow Vista360 on YouTube"
              className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-foreground transition-colors"
            >
              <Youtube className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
